import React, { useState, useRef } from 'react';
import { Pencil, ExternalLink, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePublicSettings, patchPublicSettings } from '../hooks/usePublicSettings';
import { useEditMode } from './InlineEditable';

/**
 * Extract a YouTube video ID from any supported URL format.
 * Accepts:
 *   - https://www.youtube.com/watch?v=XXXX
 *   - https://youtu.be/XXXX
 *   - https://www.youtube.com/embed/XXXX
 *   - https://www.youtube.com/shorts/XXXX
 *   - raw ID (11 chars)
 */
export function extractYoutubeId(input) {
  if (!input) return '';
  const s = String(input).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  try {
    const url = new URL(s);
    if (url.hostname.includes('youtu.be')) return url.pathname.slice(1).split('/')[0];
    if (url.searchParams.get('v')) return url.searchParams.get('v');
    const parts = url.pathname.split('/').filter(Boolean);
    const key = ['embed', 'shorts', 'v'].find(k => parts.includes(k));
    if (key) {
      const idx = parts.indexOf(key);
      return parts[idx + 1] || '';
    }
  } catch {
    // fallthrough
  }
  return '';
}

/**
 * Admin-only pencil button to edit a YouTube video (id or url).
 * Renders nothing for non-admins.
 */
export function YoutubeVideoEditButton({ settingKey, currentValue, className = '', label = 'Videoyu Değiştir' }) {
  const canEdit = useEditMode();
  const settings = usePublicSettings();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [cover, setCover] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const coverKey = `${settingKey}_cover`;

  if (!canEdit) return null;

  const openModal = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setValue(currentValue || '');
    setCover((settings[coverKey] || '').trim());
    setOpen(true);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.adminUploadFile(file);
      setCover(r.url);
      toast.success('Kapak görseli yüklendi');
    } catch {
      toast.error('Görsel yüklenemedi');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const id = extractYoutubeId(value);
      if (!id) {
        toast.error('Geçerli bir YouTube linki veya ID gir');
        setSaving(false);
        return;
      }
      await api.adminUpsertSetting(settingKey, id);
      await api.adminUpsertSetting(coverKey, (cover || '').trim());
      patchPublicSettings({ [settingKey]: id, [coverKey]: (cover || '').trim() });
      toast.success('Video güncellendi');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid={`yt-edit-${settingKey}`}
        title="Videoyu değiştir"
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/95 text-ink-600 hover:bg-white shadow-lg text-xs font-bold uppercase tracking-widest transition-colors ${className}`}
      >
        <Pencil className="w-3 h-3" /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-ink-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid={`yt-edit-modal-${settingKey}`}
        >
          <div className="bg-white text-ink-600 rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="p-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">YouTube</div>
              <h3 className="font-display text-3xl mt-2">Videoyu Değiştir</h3>
              <p className="mt-2 text-ink-400 text-sm leading-relaxed">
                YouTube&apos;dan videonun tam URL&apos;sini yapıştır. Video ID&apos;si otomatik çıkarılır.
              </p>

              <div className="mt-6">
                <label className="text-[10px] tracking-widest uppercase text-ink-400 font-bold">YouTube URL veya Video ID</label>
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid={`yt-edit-input-${settingKey}`}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="mt-2 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600 font-mono text-sm"
                  autoFocus
                />
                <div className="mt-2 text-[11px] text-ink-400 leading-relaxed">
                  Desteklenen formatlar: <span className="font-mono">youtube.com/watch?v=…</span>, <span className="font-mono">youtu.be/…</span>, <span className="font-mono">youtube.com/embed/…</span> veya sadece 11-karakter video ID.
                </div>
                {value && extractYoutubeId(value) && (
                  <div className="mt-4 aspect-video rounded-xl overflow-hidden bg-ink-50 border border-ink-100">
                    <img
                      src={cover || `https://img.youtube.com/vi/${extractYoutubeId(value)}/hqdefault.jpg`}
                      alt="Önizleme"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Cover image (optional) */}
                <div className="mt-6">
                  <label className="text-[10px] tracking-widest uppercase text-ink-400 font-bold">Kapak Görseli (opsiyonel)</label>
                  <p className="mt-1 text-[11px] text-ink-400 leading-relaxed">Boş bırakılırsa kapak, videonun YouTube görselinden otomatik oluşturulur.</p>
                  <input
                    value={cover}
                    onChange={(e) => setCover(e.target.value)}
                    data-testid={`yt-cover-input-${settingKey}`}
                    placeholder="https://... görsel adresi yapıştır veya aşağıdan yükle"
                    className="mt-2 w-full h-11 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-ink-600 text-xs font-mono"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid={`yt-cover-upload-${settingKey}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-ink-600 text-cream text-xs font-semibold hover:bg-ink-500 disabled:opacity-60"
                    >
                      <Upload className="w-3.5 h-3.5" /> {uploading ? 'Yükleniyor…' : 'Bilgisayarımdan Yükle'}
                    </button>
                    {cover && (
                      <button
                        type="button"
                        onClick={() => setCover('')}
                        data-testid={`yt-cover-remove-${settingKey}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-ink-200 text-ink-500 text-xs font-semibold hover:text-ink-600"
                      >
                        <X className="w-3.5 h-3.5" /> Kapağı Kaldır (otomatik oluştur)
                      </button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-5 bg-cream-200 flex items-center justify-between gap-3">
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-ink-500 hover:text-ink-600 font-semibold inline-flex items-center gap-1"
              >
                YouTube&apos;u Aç <ExternalLink className="w-3 h-3" />
              </a>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-11 px-5 rounded-full text-ink-500 hover:text-ink-600 text-sm font-semibold"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid={`yt-edit-save-${settingKey}`}
                  className="h-11 px-6 rounded-full bg-ink-600 text-cream text-sm font-bold hover:bg-ink-500 disabled:opacity-60"
                >
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
